import { type ChatMessageType } from "@/api/chat";
import { useUndoWidget } from "@/api/dashboards";
import { useAICapStatus } from "@/api/organizations";
import { ButtonLoading } from "@/components/ui/button-loading";
import { ResizableTextarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMutation } from "@tanstack/react-query";
import {
  BotIcon,
  BotOff,
  GripVerticalIcon,
  Minimize2Icon,
  PaperclipIcon,
  SendIcon,
  ChevronDown,
} from "lucide-react";
import React from "react";
import { useDropzone } from "react-dropzone";

import { useUser } from "@/api/user";
import { useTypingDetection } from "@/lib/utils";
import {
  type AgentAttachment,
  type AgentViewDescription,
  type OutgoingAgentMessage,
  useAgentContext,
} from "@/providers/agent-provider";
import { useAppContext } from "@/providers/app-context";
import ChatMessage from "./chats/chat-message";

const MIN_CHAT_WIDTH = 320;
const MAX_CHAT_WIDTH = 800;
const DEFAULT_CHAT_WIDTH = 380;
const CHAT_WIDTH_STORAGE_KEY = "forgeiq-chat-width";

const AgentSession = ({
  currentViewDescription = null,
  onMinimizedChange,
  onWidthChange,
  isMinimized: isMinimizedProp,
}: {
  currentViewDescription?: AgentViewDescription;
  onMinimizedChange?: (isMinimized: boolean) => void;
  onWidthChange?: (width: number) => void;
  isMinimized?: boolean;
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [inputMessage, setInputMessage] = React.useState("");
  const formRef = React.useRef<HTMLFormElement>(null);
  const [inputKey, setInputKey] = React.useState(0);
  const [isMinimized, setIsMinimized] = React.useState(isMinimizedProp ?? true);
  const [attachments, setAttachments] = React.useState<AgentAttachment[]>([]);

  // Resizable width state
  const [chatWidth, setChatWidth] = React.useState(() => {
    const saved = localStorage.getItem(CHAT_WIDTH_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_CHAT_WIDTH;
  });
  const [isResizing, setIsResizing] = React.useState(false);

  const {
    agentSeedMessage,
    clearAgentSeedMessage,
    agentContextOverride,
    clearAgentContextOverride,
  } = useAppContext();

  // Persistent context override for cross-agent routing (e.g. dashboard agent from project page)
  const [contextOverride, setContextOverride] =
    React.useState<AgentViewDescription>(null);

  // Sync internal state with external prop when it changes
  React.useEffect(() => {
    if (isMinimizedProp !== undefined) {
      setIsMinimized(isMinimizedProp);
    }
  }, [isMinimizedProp]);

  // Consume seed message and context override from AppContext
  React.useEffect(() => {
    if (agentSeedMessage) {
      setInputMessage(agentSeedMessage);
      clearAgentSeedMessage();
    }
  }, [agentSeedMessage, clearAgentSeedMessage]);

  React.useEffect(() => {
    if (agentContextOverride) {
      setContextOverride(agentContextOverride);
      clearAgentContextOverride();
    }
  }, [agentContextOverride, clearAgentContextOverride]);

  // Clear context override when user navigates (currentViewDescription changes)
  const prevViewDescRef = React.useRef(currentViewDescription);
  React.useEffect(() => {
    if (prevViewDescRef.current !== currentViewDescription) {
      prevViewDescRef.current = currentViewDescription;
      if (contextOverride) setContextOverride(null);
    }
  }, [currentViewDescription, contextOverride]);

  const [initialScrollDone, setInitialScrollDone] = React.useState(false);
  const [isTyping, resetTyping] = useTypingDetection(inputMessage, 2000);
  const [hasUnreadBelow, setHasUnreadBelow] = React.useState(false);
  const currentUser = useUser();
  const { costCapReached: aiCapReached, message: costCapStatusMessage } =
    useAICapStatus();

  React.useEffect(() => {
    onMinimizedChange?.(isMinimized);
    return () => {
      onMinimizedChange?.(true);
    };
  }, [isMinimized, onMinimizedChange]);

  // Resize handling
  React.useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.min(Math.max(newWidth, MIN_CHAT_WIDTH), MAX_CHAT_WIDTH);
      setChatWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem(CHAT_WIDTH_STORAGE_KEY, chatWidth.toString());
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, chatWidth]);

  // Notify parent of width changes
  React.useEffect(() => {
    onWidthChange?.(chatWidth);
  }, [chatWidth, onWidthChange]);

  const { messages, sendMessage, setCurrentViewDescription } =
    useAgentContext();

  React.useEffect(() => {
    setCurrentViewDescription(currentViewDescription ?? null);
  }, [setCurrentViewDescription, currentViewDescription]);

  React.useEffect(() => {
    // const { offsetHeight, scrollHeight, scrollTop } =
    //   scrollRef.current as HTMLDivElement;
    setInputKey((prev) => prev + 1);
    setInitialScrollDone(false);
  }, []);

  React.useEffect(() => {
    // scroll to the bottom everytime we get a new message
    if (!scrollRef.current) return;
    const { offsetHeight, scrollHeight, scrollTop } =
      scrollRef.current as HTMLDivElement;

    if (!initialScrollDone && messages && messages.length > 0) {
      scrollRef.current?.scrollTo({
        top: scrollHeight,
        left: 0,
      });
      setInitialScrollDone(true);
    }

    if (scrollHeight <= scrollTop + offsetHeight + 400) {
      scrollRef.current?.scrollTo({
        top: scrollHeight,
        left: 0,
        behavior: "smooth",
      });
      setHasUnreadBelow(false);
    } else {
      // show indicator of new message
      setHasUnreadBelow(true);
    }
  }, [messages, initialScrollDone]);

  const scrollToBottom = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      left: 0,
      behavior: "smooth",
    });
    setHasUnreadBelow(false);
  };

  const mutation = useMutation({
    mutationFn: async (payload: OutgoingAgentMessage) => {
      sendMessage?.(payload);
    },
  });

  const MAX_ATTACHMENT_SIZE = 4 * 1024 * 1024; // 4MB

  const fileToBase64 = React.useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1] ?? "";
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }, []);

  const processFiles = React.useCallback(
    async (files: File[]) => {
      const processed: AgentAttachment[] = [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          continue;
        }
        if (file.size > MAX_ATTACHMENT_SIZE) {
          console.warn(
            `Skipping file ${file.name} because it exceeds ${MAX_ATTACHMENT_SIZE / (1024 * 1024)}MB`,
          );
          continue;
        }
        try {
          const base64 = await fileToBase64(file);
          processed.push({
            filename: file.name,
            mime_type: file.type || "image/png",
            data: base64,
            size: file.size,
          });
        } catch (error) {
          console.error("Failed to process attachment", error);
        }
      }

      if (processed.length) {
        setAttachments((prev) => [...prev, ...processed]);
      }
    },
    [fileToBase64, MAX_ATTACHMENT_SIZE],
  );

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      void processFiles(acceptedFiles);
    },
    [processFiles],
  );

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.clipboardData?.files ?? []).filter(
        (file) => file.type.startsWith("image/"),
      );

      if (!files.length) {
        return;
      }

      const clipboardText = event.clipboardData?.getData("text") ?? "";
      if (!clipboardText.trim()) {
        event.preventDefault();
      }

      void processFiles(files);
    },
    [processFiles],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { "image/*": [] },
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop,
  });

  const rootProps = getRootProps({
    onClick: () => undefined,
    className: undefined,
  });

  const dropzoneRef = React.useRef<HTMLDivElement>(null);
  const restRootProps = rootProps;
  const inputProps = getInputProps();

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle the form submission for sending a new message
  const effectiveContext = contextOverride ?? currentViewDescription;
  const agentAvailable = Boolean(
    effectiveContext?.agent && effectiveContext?.context,
  );

  const canSendMessages = !aiCapReached && agentAvailable;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!canSendMessages) return;
    if (inputMessage.trim() === "" && attachments.length === 0) return;
    mutation.mutate({
      message: inputMessage,
      attachments,
      ...(contextOverride ? { context: contextOverride } : {}),
    });
    resetTyping();
    setInputMessage("");
    setAttachments([]);
  };

  const handleChange = (
    event:
      | React.KeyboardEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    if (
      event.type === "keyup" &&
      "key" in event &&
      event.key === "Enter" &&
      !event.shiftKey
    ) {
      event.preventDefault();
      event.stopPropagation();
      formRef.current?.requestSubmit();
      return;
    } else if (
      event.type === "keyup" &&
      "key" in event &&
      event.key === "Enter" &&
      event.shiftKey
    ) {
      setInputMessage((event.target as HTMLTextAreaElement).value);
      return;
    } else if ((event.target as HTMLTextAreaElement).value.at(-1) === "\n") {
      if (inputMessage !== "")
        setInputMessage((event.target as HTMLTextAreaElement).value);
    } else {
      setInputMessage((event.target as HTMLTextAreaElement).value);
    }
  };

  const { submitEvent } = useAgentContext();

  const handleEscalationConfirm = React.useCallback(
    (escalation: NonNullable<ChatMessageType["escalation"]>) => {
      const dashboardContext = {
        agent: "dashboard",
        known_vars: {
          dashboard_id: escalation.dashboard_id,
          dashboard_name: escalation.dashboard_name || "",
          source_widget_id: escalation.widget_id || "",
          modify_existing: true,
        },
      };
      setCurrentViewDescription(dashboardContext);
      // Natural language hint for the LLM — structured enforcement is in known_vars
      const widgetRef = escalation.widget_name
        ? `, for the widget "${escalation.widget_name}"`
        : "";
      sendMessage?.({
        message: `On this dashboard${widgetRef}: ${escalation.original_prompt}`,
        context: dashboardContext,
        silent: true,
        handoffMessage: "Switching to dashboard mode...",
      });
    },
    [setCurrentViewDescription, sendMessage],
  );

  const undoWidgetMutation = useUndoWidget();

  const handleUndoWidget = React.useCallback(
    async (dashboardId: string, widgetId: string) => {
      try {
        await undoWidgetMutation.mutateAsync({ dashboardId, widgetId });
      } catch {
        // Error toast handled by chat-message's finally block (sets undoState to "done")
      }
    },
    [undoWidgetMutation],
  );

  React.useEffect(() => {
    if (!currentUser.data?.id) return;

    const sendTypingEvent = () => {
      submitEvent({
        user: {
          id: currentUser.data?.id || "",
          first_name: currentUser.data?.first_name || "",
          last_name: currentUser.data?.last_name || "",
        },
        events: {
          is_typing: isTyping,
        },
      });
    };

    sendTypingEvent();

    // If user is typing, set up heartbeat to keep sending typing events
    let heartbeatInterval: NodeJS.Timeout | null = null;
    if (isTyping) {
      heartbeatInterval = setInterval(sendTypingEvent, 8000);
    }

    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [isTyping, currentUser.data, submitEvent]);

  if (isMinimized) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              {...restRootProps}
              ref={dropzoneRef as React.Ref<HTMLDivElement>}
              className={`fixed bottom-0 right-0 z-50 mx-4 my-8 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-card p-2 text-card-foreground shadow-lg transition-transform hover:scale-110 ${isDragActive ? "outline outline-2 outline-cf-foreground" : ""}`}
            >
              <input {...inputProps} />
              {aiCapReached ? (
                <BotOff
                  className="h-6 w-6"
                  onClick={() => setIsMinimized(!isMinimized)}
                />
              ) : (
                <BotIcon
                  className="h-6 w-6"
                  onClick={() => setIsMinimized(!isMinimized)}
                />
              )}
            </div>
          </TooltipTrigger>
          {aiCapReached && (
            <TooltipContent>
              {costCapStatusMessage ||
                "AI spend limit has been reached and the ForgeIQ agent is unavailable until the limit resets or increases."}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      {...restRootProps}
      ref={dropzoneRef as React.Ref<HTMLDivElement>}
      style={{ width: chatWidth }}
      className={`fixed right-0 top-0 z-50 flex h-screen flex-col gap-y-2 border bg-card pb-2 text-card-foreground ${isDragActive ? "outline outline-2 outline-cf-foreground" : ""} ${!canSendMessages ? "opacity-70" : ""}`}
    >
      {/* Resize handle */}
      <div
        className={`absolute left-0 top-0 flex h-full w-3 cursor-ew-resize items-center justify-center hover:bg-primary/10 ${isResizing ? "bg-primary/20" : ""}`}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizing(true);
        }}
      >
        <GripVerticalIcon className="h-4 w-4 text-muted-foreground/50" />
      </div>
      <input {...inputProps} />
      <div>
        <Minimize2Icon
          className="absolute right-4 top-4 h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
          onClick={() => setIsMinimized(!isMinimized)}
        />
      </div>
      <div
        ref={scrollRef}
        className="flex h-full max-h-full flex-col overflow-y-auto pb-6 pr-2 pt-6 scrollbar-thin scrollbar-thumb-neutral-300"
      >
        <div className="mt-auto space-y-8 px-2">
          {messages?.map((message: ChatMessageType, index: number) => {
            return (
              <ChatMessage
                key={message.id || `msg-${index}`}
                message={message}
                onEscalationConfirm={handleEscalationConfirm}
                onUndoWidget={handleUndoWidget}
              />
            );
          })}
        </div>
      </div>
      <div className="relative mx-6">
        {hasUnreadBelow && (
          <div className="mb-2 flex justify-center">
            <button
              onClick={scrollToBottom}
              className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-lg transition-all hover:bg-primary/90"
            >
              <ChevronDown className="h-3 w-3" />
              New messages below
            </button>
          </div>
        )}
        {!agentAvailable && (
          <div className="mb-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-center text-sm text-orange-800">
            <div className="flex items-center justify-center gap-2">
              <BotOff className="h-4 w-4" />
              <span>Agent not yet available on this view</span>
            </div>
          </div>
        )}
        {aiCapReached && (
          <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-800">
            <div className="flex items-center justify-center gap-2">
              <BotOff className="h-4 w-4 flex-shrink-0" />
              <span>
                {costCapStatusMessage ||
                  "AI Cost Cap has been reached and the ForgeIQ agent is off until the cap is removed"}
              </span>
            </div>
          </div>
        )}
        <IsTypingComponent />
      </div>
      <div className="flex flex-col gap-2 px-2 pt-0" key={inputKey}>
        {attachments.length ? (
          <div className="flex flex-col gap-2">
            {attachments.map((attachment, index) => (
              <div
                key={`${attachment.filename}-${index}`}
                className="flex items-center justify-between gap-2 rounded-md border border-muted-foreground/30 bg-muted/30 p-2 text-xs text-muted-foreground"
              >
                <div className="flex items-center gap-2">
                  <img
                    alt={attachment.filename}
                    src={`data:${attachment.mime_type};base64,${attachment.data}`}
                    className="h-10 w-10 rounded object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">
                      {attachment.filename}
                    </span>
                    <span>
                      {(attachment.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="text-foreground/70 hover:text-foreground"
                  onClick={() => handleRemoveAttachment(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <form
          className="flex w-full items-center space-x-2"
          onSubmit={onSubmit}
          ref={formRef}
        >
          <ResizableTextarea
            autoFocus
            id="message"
            autoComplete="off"
            value={inputMessage}
            onKeyUp={handleChange}
            onChange={handleChange}
            onPaste={handlePaste}
            disabled={!canSendMessages}
          />
          <button
            type="button"
            onClick={open}
            disabled={!canSendMessages}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-muted-foreground/40 text-muted-foreground transition-colors hover:border-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PaperclipIcon className="h-4 w-4" />
            <span className="sr-only">Attach image</span>
          </button>
          <ButtonLoading
            size="icon"
            type="submit"
            disabled={
              (inputMessage.trim() === "" && attachments.length === 0) ||
              !canSendMessages
            }
            isLoading={mutation.isPending}
            className="bg-cf-foreground hover:bg-cf-foreground/80"
            title={aiCapReached ? costCapStatusMessage : undefined}
          >
            <SendIcon className="h-4 w-4" />
            <span className="sr-only">Send</span>
          </ButtonLoading>
        </form>
      </div>
    </div>
  );
};

const IsTypingComponent = () => {
  const { usersTyping } = useAgentContext();
  const [aiUsersTyping, setAiUsersTyping] = React.useState<
    {
      first_name: string;
      last_name: string;
      id: string;
    }[]
  >([]);
  const [humanUsersTyping, setHumanUsersTyping] = React.useState<
    {
      first_name: string;
      last_name: string;
      id: string;
    }[]
  >([]);

  React.useEffect(() => {
    setAiUsersTyping(
      usersTyping.filter((u) => u.id === null && u.first_name === "ForgeIQ"),
    );
    setHumanUsersTyping(usersTyping.filter((u) => u.id !== null));
  }, [usersTyping]);

  const bouncingDots = (
    <div>
      <div className="flex w-fit flex-row p-1 text-sm">
        <div className="animate-bounce delay-100 duration-1000 ease-in-out">
          ·
        </div>
        <div className="animate-bounce delay-150 duration-1000 ease-in-out">
          ·
        </div>
        <div className="animate-bounce delay-200 duration-1000 ease-in-out">
          ·
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex gap-x-2">
      {/* Human users typing indicator */}
      {humanUsersTyping.length > 0 && (
        <div className="flex h-8 w-fit items-center gap-x-1 rounded-lg bg-muted px-2 text-xs text-muted-foreground">
          <span>
            {humanUsersTyping
              .map((u) => `${u.first_name} ${u.last_name}`)
              .join(", ")}
          </span>
          {bouncingDots}
        </div>
      )}

      {/* AI users typing indicator */}
      {aiUsersTyping.length > 0 && (
        <div className="flex h-8 w-fit items-center gap-x-1 rounded-lg bg-cf-foreground px-2 text-xs text-white/90">
          <BotIcon className="h-4 w-4 text-white" />
          <span>
            {aiUsersTyping
              .map((u) => `${u.first_name} ${u.last_name}`)
              .join(", ")}
          </span>
          {bouncingDots}
        </div>
      )}
    </div>
  );
};

export default AgentSession;
